/**
 * Shared bottom-sheet primitives for the rider modal family.
 *
 * Uses the Gogoro NP / LoopX-aligned riderTheme tokens. Composes a magazine-
 * grade modal vocabulary: drag handle, eyebrow microtype, headline, sections,
 * template cards, primary CTA, footer strip, and status views.
 */

import styled, { keyframes, css } from 'styled-components';
import { r } from './riderTheme';

// ─── Animations ─────────────────────────────────────────────────────────────
// Internal only — not exported so the file stays component-only for
// React Refresh.

const sheetEnter = keyframes`
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const stagger = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

// ─── Sheet shell ────────────────────────────────────────────────────────────

export const Sheet = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  background: ${({ theme }) =>
    `linear-gradient(180deg, ${r(theme).surface.sheet} 0%, ${r(theme).surface.sheetEdge} 100%)`};
  color: ${({ theme }) => r(theme).text.primary};
  padding: 10px 24px max(24px, env(safe-area-inset-bottom)) 24px;
  font-family: 'Geist', system-ui, sans-serif;
  font-feature-settings: 'ss01', 'cv11';
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  overflow: hidden;
  animation: ${sheetEnter} 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
  box-shadow:
    0 -1px 0 ${({ theme }) => r(theme).surface.edge},
    0 -20px 60px -20px rgba(18, 20, 25, 0.18);
`;

export const SheetScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  margin: 0 -4px;
  padding: 0 4px 4px;
  scrollbar-width: thin;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => r(theme).surface.edge};
    border-radius: 2px;
  }
`;

export const SheetSticky = styled.div`
  flex-shrink: 0;
  padding-top: 10px;
`;

export const DragHandle = styled.div`
  width: 44px;
  height: 4px;
  background: ${({ theme }) => r(theme).surface.edgeStrong};
  border-radius: 2px;
  margin: 4px auto 18px;
  flex-shrink: 0;
`;

// Top-right close affordance for keyboard / screen-reader users; mask-tap
// alone is not discoverable on mobile.
export const CloseButton = styled.button.attrs({
  type: 'button' as const,
  'aria-label': 'Close',
})`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 50%;
  background: ${({ theme }) => r(theme).surface.sheet};
  color: ${({ theme }) => r(theme).text.muted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: 'Geist', system-ui, sans-serif;
  font-size: 20px;
  line-height: 1;
  padding: 0;
  z-index: 2;
  transition: background 160ms ease, color 160ms ease, border-color 160ms ease;
  &:hover {
    background: ${({ theme }) => r(theme).surface.inputIdle};
    color: ${({ theme }) => r(theme).text.primary};
    border-color: ${({ theme }) => r(theme).surface.edgeStrong};
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
    color: ${({ theme }) => r(theme).text.primary};
  }
`;

// Left rail accent — subtle lime fade
export const LimeRail = styled.div`
  position: absolute;
  top: 22px;
  bottom: 22px;
  left: 0;
  width: 2px;
  background: ${({ theme }) => `linear-gradient(
    180deg,
    transparent 0%,
    ${r(theme).accent.limeDeep} 22%,
    ${r(theme).accent.lime} 50%,
    ${r(theme).accent.limeDeep} 78%,
    transparent 100%
  )`};
  opacity: 0.55;
  pointer-events: none;
`;

// ─── Header pieces ──────────────────────────────────────────────────────────

export const Header = styled.header`
  position: relative;
  margin-bottom: 22px;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 60ms backwards;
`;

export const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 12px;
`;

export const EyebrowMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1px solid ${({ theme }) => r(theme).accent.limeDeep};
  color: ${({ theme }) => r(theme).accent.ok};
  background: ${({ theme }) => r(theme).surface.raisedSelected};
  border-radius: 50%;
  font-family: 'Geist', sans-serif;
  font-weight: 600;
  font-size: 10px;
  line-height: 1;
  letter-spacing: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

export const Title = styled.h2`
  font-family: 'Geist', sans-serif;
  font-weight: 600;
  font-size: 30px;
  letter-spacing: -0.028em;
  line-height: 1.05;
  margin: 0;
  color: ${({ theme }) => r(theme).text.primary};
`;

export const TitleAccent = styled.span`
  color: ${({ theme }) => r(theme).accent.ok};
`;

type DotColor = 'lime' | 'aqua' | 'ok' | 'info';

const dotColorOf = (theme: unknown, c?: DotColor): string => {
  const t = r(theme);
  switch (c) {
    case 'aqua': return t.accent.aqua;
    case 'info': return t.accent.info;
    case 'lime': return t.accent.lime;
    default: return t.accent.ok;
  }
};

export const RightLockup = styled.div<{ $dotColor?: DotColor }>`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme, $dotColor }) => dotColorOf(theme, $dotColor)};
    box-shadow: 0 0 6px ${({ theme, $dotColor }) => dotColorOf(theme, $dotColor)};
  }
`;

// ─── Section primitives ────────────────────────────────────────────────────

export const Section = styled.section<{ $delay?: number }>`
  margin-bottom: 20px;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1)
    ${p => 120 + (p.$delay ?? 0)}ms backwards;
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 12px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) =>
      `linear-gradient(90deg, ${r(theme).surface.edge}, transparent)`};
  }
`;

// Wallet balance row — label + value, hairline rule above
export const BalanceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 4px 0 2px;
`;

export const BalanceLabel = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

export const BalanceValue = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.015em;
  color: ${({ theme }) => r(theme).text.primary};
`;

// ─── Template card primitives ─────────────────────────────────────────────

export const TemplateCard = styled.button<{ $selected: boolean; $affordable: boolean }>`
  position: relative;
  display: block;
  width: 100%;
  padding: 16px 18px 14px;
  background: ${({ theme, $selected }) =>
    $selected ? r(theme).surface.raisedSelected : r(theme).surface.raised};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? r(theme).text.primary : r(theme).surface.edge};
  border-radius: 14px;
  color: ${({ theme }) => r(theme).text.primary};
  text-align: left;
  cursor: ${({ $affordable }) => ($affordable ? 'pointer' : 'not-allowed')};
  opacity: ${({ $affordable }) => ($affordable ? 1 : 0.55)};
  transition:
    transform 160ms cubic-bezier(0.2, 0, 0, 1),
    border-color 200ms ease,
    background 200ms ease,
    box-shadow 200ms ease;
  margin-bottom: 10px;
  font-family: inherit;
  overflow: hidden;

  &:active {
    transform: ${({ $affordable }) => ($affordable ? 'scale(0.99)' : 'none')};
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      box-shadow:
        0 0 0 3px ${r(theme).glow.selectionLime},
        inset 0 1px 0 rgba(255, 255, 255, 0.7);

      &::after {
        content: '';
        position: absolute;
        top: 12px;
        right: 12px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${r(theme).accent.lime};
        box-shadow: 0 0 10px ${r(theme).accent.lime};
      }
    `}
`;

export const TemplateName = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 4px;
`;

export const TemplateDesc = styled.p`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.45;
  color: ${({ theme }) => r(theme).text.muted};
  margin: 0 0 10px;
`;

export const StatsRow = styled.div`
  display: flex;
  align-items: center;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 4px 0;
`;

export const StatChip = styled.span`
  display: inline-flex;
  align-items: center;

  & + & {
    &::before {
      content: '·';
      margin: 0 8px;
      color: ${({ theme }) => r(theme).text.faint};
    }
  }
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

export const PriceLabel = styled.span`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

export const PriceValue = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => r(theme).text.primary};

  & > small {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.2em;
    color: ${({ theme }) => r(theme).text.faint};
    margin-right: 8px;
    vertical-align: 0.18em;
  }
`;

export const InsufficientPill = styled.span`
  display: inline-block;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).accent.alert};
  margin-top: 6px;

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 6px;
    vertical-align: 0.15em;
  }
`;

// ─── CTA ───────────────────────────────────────────────────────────────────

export const CTAButton = styled.button<{ $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 56px;
  padding: 0 22px;
  background: ${({ theme }) => r(theme).cta.bg};
  border: 1px solid ${({ theme }) => r(theme).cta.border};
  border-radius: 14px;
  color: ${({ theme }) => r(theme).cta.text};
  font-family: 'Geist', sans-serif;
  cursor: ${p => (p.$disabled ? 'not-allowed' : 'pointer')};
  overflow: hidden;
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1), background 180ms ease;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 220ms backwards;
  box-shadow: 0 10px 24px -10px rgba(18, 20, 25, 0.45);

  ${p =>
    p.$disabled &&
    css`
      opacity: 0.4;
      filter: saturate(0.6);
    `}

  &:not(:disabled):hover {
    background: ${({ theme }) => r(theme).cta.bgHover};
    transform: translateY(-1px);
  }
  &:not(:disabled):active {
    transform: translateY(0) scale(0.99);
  }
`;

export const CTAContent = styled.span`
  display: flex;
  align-items: baseline;
  gap: 10px;
  z-index: 1;
`;

export const CTALabel = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 10px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  color: rgba(255, 255, 255, 0.55);
`;

export const CTAAmount = styled.span`
  font-family: 'Geist', sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
  color: ${({ theme }) => r(theme).cta.text};
`;

export const CTAChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => r(theme).accent.lime};
  color: ${({ theme }) => r(theme).cta.bg};
  font-family: 'Geist', sans-serif;
  font-size: 16px;
  font-weight: 600;
  z-index: 1;
  transition: transform 180ms cubic-bezier(0.2, 0, 0, 1);
  box-shadow: 0 0 0 1px rgba(192, 223, 22, 0.5);

  ${CTAButton}:not(:disabled):hover & {
    transform: translateX(2px);
  }
`;

// ─── Footer ────────────────────────────────────────────────────────────────

export const Footer = styled.footer`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => r(theme).surface.edge};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) 280ms backwards;
`;

export const FooterDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${p => p.$color};
`;

// ─── Notice (warning / info) ─────────────────────────────────────────────

export const Notice = styled.div<{ $tone?: 'info' | 'warn' }>`
  position: relative;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${({ $tone }) =>
    $tone === 'warn'
      ? 'rgba(255, 25, 35, 0.04)'
      : 'rgba(0, 215, 255, 0.05)'};
  border: 1px solid ${({ $tone }) =>
    $tone === 'warn'
      ? 'rgba(255, 25, 35, 0.18)'
      : 'rgba(0, 215, 255, 0.24)'};
  margin-bottom: 18px;
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => r(theme).text.primary};

  & > strong {
    display: block;
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    font-weight: 500;
    color: ${({ theme, $tone }) =>
      $tone === 'warn' ? r(theme).accent.alert : r(theme).accent.info};
    margin-bottom: 6px;
  }
`;

/** Inline action inside a Notice - for a recovery path the rider can take now
 * (e.g. buying the pack a blocked purchase depends on). */
export const NoticeAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 7px 12px;
  border-radius: 9px;
  border: 1px solid ${({ theme }) => r(theme).accent.info};
  background: transparent;
  cursor: pointer;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => r(theme).accent.info};
  transition: background 160ms ease;

  &:hover {
    background: rgba(0, 215, 255, 0.08);
  }

  &::after {
    content: '→';
    letter-spacing: 0;
  }
`;

// ─── Status views ───────────────────────────────────────────────────────

export const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 0 8px;
  animation: ${stagger} 380ms cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
`;

type StatusVariant = 'processing' | 'success' | 'failed';

const statusBg = (v: StatusVariant): string => {
  if (v === 'success') return 'rgba(0, 185, 70, 0.10)';
  if (v === 'failed') return 'rgba(255, 25, 35, 0.10)';
  return 'rgba(18, 18, 21, 0.05)';
};

const statusBorder = (theme: unknown, v: StatusVariant): string => {
  const t = r(theme);
  if (v === 'success') return t.accent.ok;
  if (v === 'failed') return t.accent.alert;
  return t.text.muted;
};

export const StatusRing = styled.div<{ $variant: StatusVariant }>`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $variant }) => statusBg($variant)};
  border: 1px solid ${({ theme, $variant }) => statusBorder(theme, $variant)};

  ${({ $variant, theme }) =>
    $variant === 'processing' &&
    css`
      &::before {
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 50%;
        border: 1px solid transparent;
        border-top-color: ${r(theme).accent.lime};
        border-right-color: ${r(theme).accent.lime};
        animation: ${ringSpin} 1.2s linear infinite;
      }
    `}
`;

export const StatusGlyph = styled.span<{ $variant: StatusVariant }>`
  font-family: 'Geist', sans-serif;
  font-size: 26px;
  line-height: 1;
  font-weight: 600;
  color: ${({ theme, $variant }) => {
    const t = r(theme);
    if ($variant === 'success') return t.accent.ok;
    if ($variant === 'failed') return t.accent.alert;
    return t.accent.lime;
  }};
`;

export const StatusTitle = styled.h3`
  font-family: 'Geist', sans-serif;
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.025em;
  margin: 0 0 8px;
  color: ${({ theme }) => r(theme).text.primary};
`;

export const StatusBody = styled.p`
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => r(theme).text.muted};
  margin: 0 0 20px;
  max-width: 280px;
`;

export const StatusKv = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;
  margin: 0 0 22px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.muted};

  & > strong {
    font-family: 'Geist', sans-serif;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: -0.01em;
    text-transform: none;
    color: ${({ theme }) => r(theme).text.primary};
  }
`;

export const StatusButton = styled.button`
  height: 46px;
  padding: 0 32px;
  background: transparent;
  border: 1px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-radius: 12px;
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 200ms ease;

  &:hover {
    background: rgba(18, 18, 21, 0.04);
  }
`;

// ─── Loading + empty ────────────────────────────────────────────────────

export const LoadingShell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;

  & > div {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => r(theme).surface.edge};
    border-top-color: ${({ theme }) => r(theme).accent.lime};
    animation: ${ringSpin} 0.9s linear infinite;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 28px 16px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px dashed ${({ theme }) => r(theme).surface.edge};
  border-radius: 12px;
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => r(theme).text.muted};
`;

export const ErrorLine = styled.div`
  margin-top: 12px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).accent.alert};
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${({ theme }) => r(theme).accent.alert};
  }
`;
